/**
 * What the 3D read-only plan draws, as plain data (`spec-onboarding-3d-plan-plan-scene`, R4): from
 * the layout, the floor index and the twin's BID, the viewed floor's layers and each one's draw
 * order. Pure, so the rules (only the viewed floor, nothing `_deleted`, the order) are tested
 * without WebGL; `PlanScene` only draws the list.
 */
import type {
  Entity,
  FloorPlannerDesign,
  FloorPlannerUnderlay,
  FloorPlannerFloor,
  FloorPlannerItem,
  FloorPlannerLayout,
  FloorPlannerPoint,
  FloorPlannerSurface,
  FloorPlannerWall,
} from '@repo/frontend-types';
import {
  alignFloorDrawOrder,
  findLatestDesign,
  firstLiveSurfaces,
  floorDrawState,
  PIXELS_PER_METER,
  pixelsToMeters,
  underlayImageScale,
  resolvePlanHierarchy,
  resolvePlanParentage,
  spatialParentOfSurface,
  surfaceName,
  type FloorDrawState,
  type FloorVisibility,
  type SpaceKind,
} from '@repo/front-end-utils';
import { centroidPx } from '@repo/ui/planner';
import {
  convertCoordLocalPoint,
  floorGroupTransform,
  floorWallOutlines,
  planFloorEntity,
  planMachineEntity,
  planWallEntity,
} from '@repo/ui';
import type { FloorGroupTransform } from '@repo/ui';
import type { Vector3 } from 'three';
import type { PlanMachineBox } from '../twinEntities/asset/PlanEditorMachineContext';
import { boxCentre } from './planEditorCandidates';

type Point = { x: number; y: number };

/**
 * A plan point (floor metres) → the floor group's own scene frame: the chain launch and the live
 * twin use. `scalePoint` swaps x and y and scales metres to centimetres, the factory's
 * `createCoordLocalPoint` (private, so repeated here) turns centimetres into COORD_LOCAL, and
 * `convertCoordLocalPoint` takes that into the scene. Net: scene `x = plan.x` (east), scene
 * `z = −plan.y` (plan north is scene −z). Held equal to the factory by `planWallEntity.parity.test.ts`.
 */
export const planPointToScene = (p: Point, altitude = 0): Vector3 => {
  // scalePoint: the x↔y swap into the factory frame, metres → centimetres.
  const cm = { x: p.y * 100, y: p.x * 100 };
  // createCoordLocalPoint: `cmToKm(x) * 10`.
  const coordLocal = { latitude: (cm.x / 100000) * 10, longitude: (cm.y / 100000) * 10 };
  return convertCoordLocalPoint(coordLocal, altitude);
};

/**
 * A floor's group: launch's floor entity through the live twin's own `floorGroupTransform`, at
 * `altitude ?? 0.01` as `TwinEntity3D` passes it to `Floor`. `position` is the floor's index in
 * `layout.floors`, which sets its height (`floorElevationM`). The scene draws in this group and the
 * camera solves for it, so the two never disagree about where the floor is.
 */
export const viewedFloorTransform = (
  floor: FloorPlannerFloor,
  position: number,
  twinBid: string,
): FloorGroupTransform => {
  const entity = planFloorEntity(floor, { twinBID: twinBid, position });
  return floorGroupTransform(entity, entity.altitude ?? 0.01);
};

/**
 * The plan's layers in 2D order (R4.7): a layer's `renderOrder` is `floorRank × 100 + code`. The
 * code 60 (camera cones, edit-cameras) is reserved, so that feature adds a layer without
 * renumbering.
 */
export const PLAN_LAYER = {
  /** PDF planes, 10 to 29: one code per stack index (`pdfRenderOrder`). */
  pdfs: [10, 29] as const,
  walls: 30,
  /** Spaces: 40 plus the nesting depth, 0 to 9. */
  spaces: 40,
  machines: 50,
  /** Reserved: camera cones. */
  cones: 60,
  names: 70,
  /** The selected PDF's outline and turn knob (`edit-pdf-stack`, R6.3): above everything. */
  selection: 80,
} as const;

/**
 * A selected wall's look on the 3D editor (`edit-walls`, R3.3), in the walls' band: its footprint
 * over every wall, and its cyan edge over that, both under the spaces.
 */
export const SELECTED_WALL_LAYER = {
  fill: PLAN_LAYER.walls + 1,
  edge: PLAN_LAYER.walls + 2,
} as const;

/** The deepest nesting a space's draw order counts (40 + 9 = 49). */
export const MAX_SPACE_DEPTH = 9;

export const renderOrderFor = (floorRank: number, code: number): number => floorRank * 100 + code;

/**
 * The draw order of the PDF at list index `i`, 0 at the bottom (`edit-pdf-stack`, R2.5): the codes
 * 10 to 29 in list order. From the twenty-first up they share 29, and among those three's sort keeps
 * the order they are mounted in, which is the list's.
 */
export const pdfRenderOrder = (floorRank: number, i: number): number =>
  renderOrderFor(
    floorRank,
    PLAN_LAYER.pdfs[0] + Math.min(i, PLAN_LAYER.pdfs[1] - PLAN_LAYER.pdfs[0]),
  );

/** Where a PDF's plane is drawn, in the floor group's frame. */
export interface PdfPlanePlacement {
  /** The PDF's centre on the floor. */
  position: [number, number, number];
  /**
   * Laid flat (−90° about x), then turned about the vertical by the stored rotation: the order
   * `YXZ` applies x first. The image's top edge faces plan +y at rotation 0, as `UnderlayImage`
   * draws it upright.
   */
  rotation: [number, number, number, 'YXZ'];
  /** Metres. */
  width: number;
  height: number;
}

const finite = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);

/**
 * A PDF's plane (`edit-pdf-stack`, R2.3): its centre, its turn and its size in metres, so that its
 * four corners are `underlayWorldCorners` in metres, the rule the press, the bounds and the 2D
 * drawing use. `null` where it cannot be drawn: a centre or turn that is not a finite number, or a
 * native size that is not finite and above 0 (Inputs and Missing Values).
 */
export const pdfPlanePlacement = (
  u: Pick<FloorPlannerUnderlay, 'x' | 'y' | 'rotation' | 'scale' | 'widthPx' | 'heightPx'>,
): PdfPlanePlacement | null => {
  if (!finite(u.x) || !finite(u.y) || !finite(u.rotation)) return null;
  if (!finite(u.widthPx) || u.widthPx <= 0 || !finite(u.heightPx) || u.heightPx <= 0) return null;
  const metresPerImagePixel = underlayImageScale(u) / PIXELS_PER_METER;
  const at = planPointToScene({ x: u.x, y: u.y });
  return {
    position: [at.x, at.y, at.z],
    // Plan turns run anticlockwise from east towards north; north is scene −z, so the same turn is
    // +θ about scene +y.
    rotation: [-Math.PI / 2, (u.rotation * Math.PI) / 180, 0, 'YXZ'],
    width: u.widthPx * metresPerImagePixel,
    height: u.heightPx * metresPerImagePixel,
  };
};

export interface PlanSpace {
  refid: string;
  /** The outline in floor metres, as stored. */
  poly: Point[];
  /** Steps from the floor by launch's parent rule, capped at `MAX_SPACE_DEPTH`. */
  depth: number;
  renderOrder: number;
  /**
   * Zone, section or area, as the plan's hierarchy derives it (`resolvePlanHierarchy`): the letter of
   * the space's code (`Z004`, `NPL-STN-S006`) when coded, else the kind its place dictates (on the
   * floor a zone, in a zone a section, in a section an area). `null` where neither is legal: too
   * deep, or a code whose letter no space carries.
   */
  level: SpaceKind | null;
}

/**
 * How deep each surface sits under the floor, by launch's parent rule (`PlannerConverter`): the
 * resolver's effective parent for the first copy of a refid, the drawing's for any later copy. A
 * cycle stops at the cap.
 */
const nestingDepthOf = (design: FloorPlannerDesign) => {
  const parentage = resolvePlanParentage(design);
  const candidates = firstLiveSurfaces(design.surfaces);
  const first = new Map(candidates.map(s => [s.refid, s]));
  const parentOf = (refid: string): string | null =>
    parentage.surface(refid)?.effectiveParent ?? null;
  return (surface: FloorPlannerSurface): number => {
    let parent =
      first.get(surface.refid) === surface
        ? parentOf(surface.refid)
        : spatialParentOfSurface(surface, candidates);
    let depth = 0;
    while (parent !== null && depth < MAX_SPACE_DEPTH) {
      depth += 1;
      parent = parentOf(parent);
    }
    return depth;
  };
};

/** The viewed floor's spaces: every surface not `_deleted` with three or more points. */
export const planSpaces = (design: FloorPlannerDesign, floorRank = 0): PlanSpace[] => {
  const depthOf = nestingDepthOf(design);
  // `onboarding-status-colours-plan-spaces`, R1: the kind the hierarchy gives, so an uncoded space
  // takes its place's (a later copy of a refid, its first copy's).
  const hierarchy = resolvePlanHierarchy(design);
  return (design.surfaces ?? [])
    .filter(s => !s._deleted && (s.poly?.length ?? 0) >= 3)
    .map(s => {
      const depth = depthOf(s);
      return {
        refid: s.refid,
        poly: s.poly,
        depth,
        renderOrder: renderOrderFor(floorRank, PLAN_LAYER.spaces + depth),
        level: hierarchy.surface(s.refid)?.kind ?? null,
      };
    });
};

/**
 * A CSS colour as three takes it: three's `Color` drops a CSS alpha, so `rgba(r, g, b, a)` becomes
 * its `rgb(r, g, b)` and an opacity. Anything without an alpha is opaque.
 */
export const cssColourToThree = (css: string): { color: string; opacity: number } => {
  const m = /^rgba\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*\)$/i.exec(
    css.trim(),
  );
  if (!m) return { color: css, opacity: 1 };
  return { color: `rgb(${m[1]}, ${m[2]}, ${m[3]})`, opacity: Number(m[4]) };
};

/** The read-only plan's name size, 11 world pixels: 0.22 m on the floor. */
export const PLAN_NAME_SIZE_M = pixelsToMeters(11);

/**
 * A name lies flat on the floor, reading along plan +x with its up along plan +y (scene −z), so it
 * turns with the floor and the twin as the 2D name turns with the view, and is never mirrored.
 */
export const PLAN_NAME_ROTATION: [number, number, number] = [-Math.PI / 2, 0, 0];

export interface PlanName {
  refid: string;
  text: string;
  /** Floor-group frame, at the outline's centroid. */
  position: [number, number, number];
}

/**
 * Each drawn space's name (R4.6): a non-blank `surfaceName`, at its outline's centroid as the
 * read-only plan places it (`centroidPx`). The text is as stored; the scene makes it drawable.
 */
export const planNames = (design: FloorPlannerDesign): PlanName[] =>
  (design.surfaces ?? [])
    .filter(s => !s._deleted && (s.poly?.length ?? 0) >= 3 && surfaceName(s) !== '')
    .map(s => {
      const c = centroidPx(s.poly);
      const at = planPointToScene({ x: pixelsToMeters(c.x), y: pixelsToMeters(c.y) });
      return { refid: s.refid, text: surfaceName(s), position: [at.x, at.y, at.z] };
    });

/** A machine's name on the 3D editor (`edit-machines`, R7.1): 9 world pixels, 0.18 m on the floor. */
export const PLAN_MACHINE_NAME_SIZE_M = pixelsToMeters(9);
/** The margin a machine's name keeps inside its box, each side: 0.08 m. */
const MACHINE_NAME_INSET_M = 0.08;

/** No machine hidden. */
const NO_REFIDS: ReadonlySet<string> = new Set();

export interface PlanMachineName extends PlanName {
  /** Metres: the name wraps within its box, less the inset, and never narrower than twice it. */
  maxWidth: number;
}

/**
 * Each drawn machine's name on the 3D editor (`edit-machines`, R7.1 to R7.3): a machine not
 * `_deleted` whose trimmed name is not blank, at the centre of the box it is drawn with (its point
 * plus its offset, turned as it is), on the floor. A machine whose model has not drawn yet has no box
 * and so no name. `itemShown` is the live pose, so a moving name follows its machine; a machine in
 * `hidden` (being carried, `floor-plan-machine-carry`) has none.
 */
export const planMachineNames = (
  items: readonly FloorPlannerItem[],
  boxOf: (refid: string) => PlanMachineBox | undefined,
  itemShown: (item: FloorPlannerItem) => FloorPlannerItem = item => item,
  hidden: ReadonlySet<string> = NO_REFIDS,
): PlanMachineName[] => {
  const names: PlanMachineName[] = [];
  items.forEach(stored => {
    const text = stored.name?.trim() ?? '';
    const box = boxOf(stored.refid);
    if (stored._deleted || hidden.has(stored.refid) || text === '' || !box) return;
    const item = itemShown(stored);
    const at = planPointToScene(boxCentre(item, box));
    names.push({
      refid: item.refid,
      text,
      position: [at.x, at.y, at.z],
      maxWidth: Math.max(box.width - 2 * MACHINE_NAME_INSET_M, 2 * MACHINE_NAME_INSET_M),
    });
  });
  return names;
};

/**
 * The viewed floor's machines: each item not `_deleted`, as launch's conversion makes it
 * (`planMachineEntity`), for the live twin's `HandleAsset`. `depth` only lifts the overlay, which the
 * plan does not draw. A machine in `hidden` is being carried and is drawn by the carry instead.
 */
export const planMachines = (
  design: FloorPlannerDesign,
  twinBid: string,
  itemShown: (item: FloorPlannerItem) => FloorPlannerItem = item => item,
  hidden: ReadonlySet<string> = NO_REFIDS,
): Entity[] =>
  (design.items ?? [])
    .filter(item => !item._deleted && !hidden.has(item.refid))
    .map(item => planMachineEntity(itemShown(item), { twinBID: twinBid, depth: 2 }));

/** Whether two outlines are the same points in the same order. */
const sameOutline = (
  p: readonly FloorPlannerPoint[] | undefined,
  q: readonly FloorPlannerPoint[],
) => !!p && p.length === q.length && p.every((v, i) => v.x === q[i].x && v.y === q[i].y);

/**
 * A floor's walls while a corner is dragged (`edit-walls`, R5.3; `wall-editing-look`, R1.12): the
 * floor's joined outlines worked out from the walls as shown, and each wall converted again only where
 * its outline differs from the draw list's, so a wall the drag does not reach keeps its entity (a new
 * wall entity is a new wall geometry). A dragged corner re-shapes the walls it joins too.
 */
export const planWallsShown = (
  drawList: Pick<PlanDrawList, 'walls' | 'wallOutlines' | 'floor'>,
  wallsShown: readonly FloorPlannerWall[],
  twinBid: string,
): { walls: Entity[]; outlines: FloorPlannerPoint[][] } => {
  const outlines = floorWallOutlines(wallsShown);
  return {
    walls: drawList.walls.map((entity, i) =>
      wallsShown[i] && !sameOutline(drawList.wallOutlines[i], outlines[i])
        ? planWallEntity(wallsShown[i], i, twinBid, drawList.floor.name, outlines[i])
        : entity,
    ),
    outlines,
  };
};

/** Everything the scene draws for one floor, with each layer's draw order. */
export interface PlanDrawList {
  floor: FloorPlannerFloor;
  /** The floor's rank in the draw order: every layer's order is built from it. */
  floorRank: number;
  design: FloorPlannerDesign;
  spaces: PlanSpace[];
  machines: Entity[];
  machinesOrder: number;
  walls: Entity[];
  /**
   * Each wall's joined outline, plan metres, in the walls' order (`floorWallOutlines`, the outlines
   * launch saves): what `walls` were built from, and what the selected walls are drawn round.
   */
  wallOutlines: FloorPlannerPoint[][];
  wallsOrder: number;
  names: PlanName[];
  namesOrder: number;
  /** Each PDF's draw order by its id, from its index in the floor's stack (`edit-pdf-stack`, R2.5). */
  pdfOrders: Record<string, number>;
}

/**
 * The draw list for one floor (R4.2): `layout.floors[floorIndex]` and its latest design, as the view
 * model reads it, and nothing from any other floor. `null` for a floor index past the floors; a floor
 * with no design gets `findLatestDesign`'s empty design, so draws nothing (the frame says so). One
 * floor drawn alone is rank 0; Build floors gives each drawn floor its rank
 * (`planSceneFloorDrawLists`).
 */
export const planSceneDrawList = (
  layout: FloorPlannerLayout,
  floorIndex: number,
  twinBid: string,
  floorRank = 0,
): PlanDrawList | null => {
  const floor = layout.floors[floorIndex];
  const design = floor ? findLatestDesign(floor.designs) : null;
  if (!floor || !design) return null;
  // Worked out for the whole floor: a wall's joined shape depends on the walls it meets.
  const wallOutlines = floorWallOutlines(design.walls ?? []);
  return {
    floor,
    floorRank,
    design,
    spaces: planSpaces(design, floorRank),
    machines: planMachines(design, twinBid),
    machinesOrder: renderOrderFor(floorRank, PLAN_LAYER.machines),
    // Every wall, as launch converts them: launch keeps a `_deleted` wall, so the plan does too.
    walls: (design.walls ?? []).map((wall, i) =>
      planWallEntity(wall, i, twinBid, floor.name, wallOutlines[i]),
    ),
    wallOutlines,
    wallsOrder: renderOrderFor(floorRank, PLAN_LAYER.walls),
    names: planNames(design),
    namesOrder: renderOrderFor(floorRank, PLAN_LAYER.names),
    pdfOrders: Object.fromEntries(
      (design.underlays ?? []).map((u, i) => [u.id, pdfRenderOrder(floorRank, i)]),
    ),
  };
};

/** A drawn floor in Build floors: its place in the list, its draw state and its rank. */
export interface PlanDrawnFloor {
  /** The floor's index in `layout.floors`: its height (`floorElevationM`). */
  index: number;
  state: Exclude<FloorDrawState, 'hidden'>;
}

/** One drawn floor's draw list in Build floors, with its place, rank and state. */
export interface PlanFloorDrawList extends PlanDrawList, PlanDrawnFloor {
  /** Its place in `alignFloorDrawOrder`: 0 at the bottom, the selected floor last and highest. */
  rank: number;
}

/**
 * The floors 3D Build floors draws (`onboarding-3d-plan-edit-build-floors`, R2.1), in 2D's order
 * (`alignFloorDrawOrder`): the selected floor, the floor directly below it, any floor a person shows,
 * none a person hid. The selected floor comes last.
 */
export const planSceneDrawnFloors = (
  floors: readonly FloorPlannerFloor[],
  activeFloorIndex: number,
  visibility: FloorVisibility,
): PlanDrawnFloor[] =>
  alignFloorDrawOrder(floors, activeFloorIndex, visibility).map(({ index }) => ({
    index,
    // Never `hidden`: `alignFloorDrawOrder` draws only the floors that are not.
    state: floorDrawState(floors, index, activeFloorIndex, visibility) as PlanDrawnFloor['state'],
  }));

/**
 * One draw list per drawn floor (R2.2), each ranked by its place in `drawn`, so every layer of a
 * higher rank draws after every layer of a lower one whatever the floors' heights: the selected
 * floor, last, is on top. The group's transform is not in the list: a floor being moved changes only
 * its group, never its list (R2.8).
 */
export const planSceneFloorDrawLists = (
  layout: FloorPlannerLayout,
  drawn: readonly PlanDrawnFloor[],
  twinBid: string,
): PlanFloorDrawList[] =>
  drawn.flatMap(({ index, state }, rank) => {
    const list = planSceneDrawList(layout, index, twinBid, rank);
    return list ? [{ ...list, index, state, rank }] : [];
  });
